import { useState, useEffect } from 'react';
import { ref as dbRef, onValue, set } from 'firebase/database';
import { database } from '../config/firebase';
import { CAR_CATEGORIES, MOTO_CATEGORIES } from '../config/constants';

/**
 * useSettings — Fase 3 del piano (refactor hook multi-tenant).
 *
 * CAMBIAMENTI rispetto alla versione single-tenant:
 *
 * 1. Legge da companies/{companyId}/settings invece che da 'settings' root-level.
 *
 * 2. I DEFAULT_SETTINGS sono stati ripuliti da tutti i dati hardcoded di LG Gold Rent
 *    (ragioneSociale, indirizzo, p.iva, email, ecc.) — ogni azienda parte da un
 *    default generico e sovrascrive durante l'onboarding. Questo era un problema
 *    single-tenant: i dati di LG Gold Rent venivano usati come default per tutti.
 *
 * 3. Il provider payment è stato semplificato: l'unico metodo online consentito
 *    è 'stripe' (decisione presa nel documento di piano). I campi paypal/cash/bank
 *    restano nel modello dati per compatibilità con le prenotazioni manuali già
 *    esistenti, ma non vengono più proposti come opzioni nel form.
 *
 * L'interfaccia pubblica (settings, loading, error, saveSettings) resta invariata.
 */

export const DEFAULT_SETTINGS = {
  azienda: {
    logoAzienda: '',
    ragioneSociale: '',
    indirizzo: '',
    citta: '',
    cap: '',
    lat: null,
    lng: null,
    piva: '',
    codiceFiscale: '',
    telefono: '',
    email: '',
    sito: '',
    slogan: '',
    franchisiaSinistri: '',
    franchigiaFurtoIncendio: '',
    franchigiaDanni: '',
    luogoConsegna: '',
    luogoRiconsegna: '',
    latConsegna: null,
    lngConsegna: null,
    latRiconsegna: null,
    lngRiconsegna: null,
    condizioni: 'Il cliente, firmando il presente contratto, dichiara di aver ricevuto, visionato e accettato le condizioni di noleggio.',
    // Firma dell'azienda per i contratti firmati online dal cliente (area
    // cliente, prima del ritiro): impostata una tantum qui e applicata in
    // automatico da ContractGenerator.js su ogni contratto — vedi
    // AziendaSettings.js ("Firma Azienda") e la funzione firmaContrattoCliente.
    // Campi LEGACY (pre "firme multiple"): mantenuti solo per compatibilità
    // con dati già salvati; ContractGenerator.js li migra automaticamente in
    // firmeAzienda al primo utilizzo, vedi useFirmeAzienda in ContractGenerator.js.
    firmaAzienda: '',
    firmaAziendaAggiornataIl: '',
    // Rubrica delle firme del noleggio raccolte al momento della stampa del
    // contratto (vedi ContractGenerator.js): fino a un massimo di 6 firme,
    // { id, immagine, creataIl }. All'atto della stampa l'admin sceglie quale
    // riusare oppure ne disegna una nuova (se sotto al limite).
    firmeAzienda: [],
    cargosSoggetta: false,
  },
  // Punti di ritiro/riconsegna aggiuntivi (oltre alla sede) con supplementi.
  // Nodo { [id]: {...} } — vedi shared/puntiRitiro.js. DEVE comparire sia qui
  // sia nel caricamento qui sotto: saveSettings sovrascrive l'intero nodo
  // settings, quindi un nodo non caricato verrebbe cancellato a ogni "Salva".
  puntiRitiroRiconsegna: {},
  popup: {
    titolo: 'Vuoi annullare la prenotazione?',
    messaggio: 'Per annullare la tua prenotazione contattaci entro 24 ore dalla conferma. Saremo felici di aiutarti a trovare la soluzione migliore.',
    telefono: '',
    email: '',
    orari: 'Lun - Sab: 9:00 - 18:00'
  },
  payment: {
    // L'unico metodo online consentito è 'stripe' (vedi documento di piano, sez. 3).
    // paypal/cash/bank restano nel modello per retrocompatibilità con prenotazioni manuali.
    provider: 'stripe',
    stripePublicKey: '',
  },
  terms: {
    termsText: ''
  },
  bookingConfirmation: {
    titolo: 'Prenotazione Confermata!',
    messaggio: 'Grazie per aver scelto il nostro servizio. La tua prenotazione è stata ricevuta e confermata con successo. A presto!',
    infoAggiuntive: 'Ricordati di portare un documento d\'identità valido e la patente al momento del ritiro del veicolo.',
    telefono: '',
    email: '',
    testoBottone: 'Perfetto, Grazie!'
  },
  catalogoVisibilita: {
    mostraAuto: true,
    mostraMoto: true,
    categorieAuto: [...CAR_CATEGORIES],
    categorieMoto: [...MOTO_CATEGORIES],
  },
  sito: {
    slogan: '',
    logoSito: '',
    bannerImage: '',
    coloreAccento: '#2563EB',
    coloreSecondario: '',
    telefonoPubblico: '',
    emailPubblica: '',
    indirizzoPubblico: '',
    whatsapp: '',
    instagram: '',
    orariApertura: {
      lun: { aperto: true,  apertura: '08:00', chiusura: '18:00' },
      mar: { aperto: true,  apertura: '08:00', chiusura: '18:00' },
      mer: { aperto: true,  apertura: '08:00', chiusura: '18:00' },
      gio: { aperto: true,  apertura: '08:00', chiusura: '18:00' },
      ven: { aperto: true,  apertura: '08:00', chiusura: '18:00' },
      sab: { aperto: true,  apertura: '09:00', chiusura: '13:00' },
      dom: { aperto: false, apertura: '09:00', chiusura: '13:00' },
    },
    prenotazioni: {
      accettaPrenotazioni: true,
      messaggioChiusura: 'Le prenotazioni online sono temporaneamente sospese. Contattaci direttamente per assistenza.',
      bloccaGiorniPrima: 0,
    },
  }
};

export const useSettings = (companyId) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }

    const settingsRef = dbRef(database, `companies/${companyId}/settings`);

    const unsubscribe = onValue(
      settingsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setSettings({
            azienda: { ...DEFAULT_SETTINGS.azienda, ...(data.azienda || {}) },
            popup: { ...DEFAULT_SETTINGS.popup, ...(data.popup || {}) },
            puntiRitiroRiconsegna: data.puntiRitiroRiconsegna || {},
            payment: {
              ...DEFAULT_SETTINGS.payment,
              ...(data.payment || {}),
            },
            terms: { ...DEFAULT_SETTINGS.terms, ...(data.terms || {}) },
            bookingConfirmation: { ...DEFAULT_SETTINGS.bookingConfirmation, ...(data.bookingConfirmation || {}) },
            catalogoVisibilita: {
              ...DEFAULT_SETTINGS.catalogoVisibilita,
              ...(data.catalogoVisibilita || {}),
              categorieAuto: data.catalogoVisibilita?.categorieAuto || DEFAULT_SETTINGS.catalogoVisibilita.categorieAuto,
              categorieMoto: data.catalogoVisibilita?.categorieMoto || DEFAULT_SETTINGS.catalogoVisibilita.categorieMoto,
            },
            sito: {
              ...DEFAULT_SETTINGS.sito,
              ...(data.sito || {}),
              orariApertura: {
                lun: { ...DEFAULT_SETTINGS.sito.orariApertura.lun, ...(data.sito?.orariApertura?.lun || {}) },
                mar: { ...DEFAULT_SETTINGS.sito.orariApertura.mar, ...(data.sito?.orariApertura?.mar || {}) },
                mer: { ...DEFAULT_SETTINGS.sito.orariApertura.mer, ...(data.sito?.orariApertura?.mer || {}) },
                gio: { ...DEFAULT_SETTINGS.sito.orariApertura.gio, ...(data.sito?.orariApertura?.gio || {}) },
                ven: { ...DEFAULT_SETTINGS.sito.orariApertura.ven, ...(data.sito?.orariApertura?.ven || {}) },
                sab: { ...DEFAULT_SETTINGS.sito.orariApertura.sab, ...(data.sito?.orariApertura?.sab || {}) },
                dom: { ...DEFAULT_SETTINGS.sito.orariApertura.dom, ...(data.sito?.orariApertura?.dom || {}) },
              },
              prenotazioni: {
                ...DEFAULT_SETTINGS.sito.prenotazioni,
                ...(data.sito?.prenotazioni || {}),
              },
            }
          });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Errore caricamento impostazioni:', err);
        setError(err);
        setSettings(DEFAULT_SETTINGS);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  const saveSettings = async (newSettings) => {
    if (!companyId) throw new Error('companyId mancante: impossibile salvare le impostazioni');
    try {
      await set(dbRef(database, `companies/${companyId}/settings`), newSettings);
      return true;
    } catch (err) {
      console.error('Errore salvataggio impostazioni:', err);
      throw err;
    }
  };

  return { settings, loading, error, saveSettings };
};
